import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { WorkflowItem } from "@/api/models"

import { Clock, Play, Share2, Star, StarOff, User as UserIcon, Users } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { usePrefetchWorkflow } from "@/hooks/use-prefetch"
import { cn } from "@/lib/utils"

import { ExpandableRunButtons } from "./expandable-run-buttons"
import { WorkflowMiniPreview } from "./workflow-mini-preview"
import { ScheduleButton } from "./workflow-schedule-dialog"
import { WorkflowTileDropdown } from "./workflow-tile-dropdown"

type WorkflowTileProps = {
  workflow: WorkflowItem
  canEdit: boolean
  canSchedule: boolean
  canFavorite?: boolean
  onOpen: (workflow: WorkflowItem) => void
  onDuplicate: (workflow: WorkflowItem) => void
  onToggleFavorite: (workflow: WorkflowItem) => void
  onDelete: (workflow: WorkflowItem) => void
}

// Category color mapping for visual distinction (keep for category badges)
const categoryColors: Record<string, string> = {
  Legal: CATEGORY_TONES[1],
  Support: CATEGORY_TONES[4],
  Productivity: CATEGORY_TONES[0],
  Sandbox: CATEGORY_TONES[2],
  Default: CATEGORY_TONES[3],
}

function getCategoryColor(category?: string): string {
  if (!category) return categoryColors.Default
  return categoryColors[category] || categoryColors.Default
}

const HOVER_DELAY_MS = 100

export function WorkflowTile({
  workflow,
  canEdit,
  canSchedule,
  canFavorite = true,
  onOpen,
  onDuplicate,
  onToggleFavorite,
  onDelete,
}: WorkflowTileProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const prefetchWorkflow = usePrefetchWorkflow()
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const runButtonsRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isRunExpanded, setIsRunExpanded] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Card is considered "expanded" when hovered OR when dropdown is open
  const isCardExpanded = isHovered || isDropdownOpen

  // Cleanup prefetch timeout on unmount
  useEffect(() => {
    return () => {
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    // Clear any pending timeout
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current)
    }
    // Small delay before expansion starts to avoid accidental triggers
    prefetchTimeoutRef.current = setTimeout(() => {
      setIsHovered(true)
      prefetchWorkflow(workflow.id)
      prefetchTimeoutRef.current = null
    }, HOVER_DELAY_MS)
  }

  const handleMouseLeave = () => {
    // Cancel if user moves mouse away before delay completes
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current)
      prefetchTimeoutRef.current = null
    }
    // Don't collapse if dropdown is open (it renders in a portal outside the card)
    if (!isDropdownOpen) {
      setIsHovered(false)
      setIsRunExpanded(false)
    }
  }

  // Close run buttons when clicking outside
  useEffect(() => {
    if (!isRunExpanded) return

    const handleClickOutside = (event: MouseEvent) => {
      if (runButtonsRef.current && !runButtonsRef.current.contains(event.target as Node)) {
        setIsRunExpanded(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isRunExpanded])

  // When dropdown opens, ensure the card stays expanded
  // (the hover delay might not have completed yet when user clicks the ellipsis)
  useEffect(() => {
    if (isDropdownOpen) {
      // Cancel any pending hover timeout and force hover state
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current)
        prefetchTimeoutRef.current = null
      }
      setIsHovered(true)
    }
  }, [isDropdownOpen])

  // Default to "company" for backwards compatibility with old workflows
  const visibility = workflow.visibility
  let VisibilityIcon = UserIcon
  let visibilityLabel = t("tile.personal")
  if (visibility === "company") {
    VisibilityIcon = Users
    visibilityLabel = t("tile.companyFlow")
  } else if (visibility === "shared") {
    VisibilityIcon = Share2
    visibilityLabel = t("tile.shared")
  }

  const cardContent = (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {workflow.category && (
            <span
              className={cn(
                "inline-flex items-center rounded-md px-2 py-1 text-[11px] font-medium",
                getCategoryColor(workflow.category)
              )}
            >
              {workflow.category}
            </span>
          )}
          <Badge className="gap-1.5 px-2 py-1 text-[11px]" variant="outline">
            <VisibilityIcon className="h-3 w-3" />
            <span>{visibilityLabel}</span>
          </Badge>
        </div>

        <div className="flex items-center gap-1">
          <button
            aria-label={workflow.is_favorite ? t("tile.removeFavorite") : t("tile.markAsFavorite")}
            className="bg-card text-muted-foreground hover:border-warning hover:text-warning focus-visible:ring-ring rounded-full border p-1.5 transition focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite(workflow)
            }}
          >
            {workflow.is_favorite ? (
              <Star className="fill-warning text-warning h-4 w-4" />
            ) : (
              <StarOff className="h-4 w-4" />
            )}
          </button>

          <WorkflowTileDropdown
            canEdit={canEdit}
            deleteLabel={t("tile.deleteFlow")}
            duplicateLabel={t("tile.duplicate")}
            editLabel={t("tile.edit")}
            moreActionsLabel={t("tile.moreActions", { defaultValue: "More actions" })}
            workflow={workflow}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onOpen={onOpen}
          />
        </div>
      </div>

      <h3 className="mb-2 text-base leading-tight font-semibold">{workflow.title}</h3>

      {visibility === "company" && workflow.owner_name && (
        <div className="mb-2">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium",
              "bg-muted text-muted-foreground"
            )}
          >
            <UserIcon className="h-3 w-3" />
            {workflow.owner_name}
          </span>
        </div>
      )}

      {workflow.description && (
        <p className="text-muted-foreground mb-3 line-clamp-2 text-sm leading-relaxed">
          {workflow.description}
        </p>
      )}

      {workflow.tags && workflow.tags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {workflow.tags.map((tag) => (
            <Badge key={tag} className="text-[11px]" variant="outline">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      <div className="mt-auto flex items-center justify-between border-t pt-3">
        <div className="flex items-center gap-1.5">
          <Button className="h-7 gap-1 px-2.5 text-xs font-medium" size="sm">
            <Play className="h-3 w-3" />
            {t("tile.run")}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            {workflow.last_run && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{workflow.last_run}</span>
              </span>
            )}
            {workflow.runs_this_week !== undefined && workflow.runs_this_week > 0 && (
              <span className="text-muted-foreground/70">· {workflow.runs_this_week}/week</span>
            )}
          </div>
          {canSchedule && (
            <ScheduleButton
              label={t("tile.scheduleRun")}
              workflowId={workflow.id}
              workflowTitle={workflow.title}
            />
          )}
        </div>
      </div>
    </>
  )

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div inert aria-hidden="true" className="pointer-events-none invisible p-5">
        <div className="flex flex-col">{cardContent}</div>
      </div>

      <Card
        role="presentation"
        className={cn(
          "group absolute flex cursor-pointer flex-col p-5 transition-[top,right,bottom,left,box-shadow,border-color] duration-300",
          "hover:border-primary/50",
          isCardExpanded ? "-inset-x-4 -top-20 z-20 shadow-2xl" : "inset-0 overflow-hidden"
        )}
        onClick={() => onOpen(workflow)}
      >
        <div className="flex flex-1 flex-col">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {workflow.category && (
                <span
                  className={cn(
                    "inline-flex items-center rounded-md px-2 py-1 text-[11px] font-medium",
                    getCategoryColor(workflow.category)
                  )}
                >
                  {workflow.category}
                </span>
              )}
              <Badge className="gap-1.5 px-2 py-1 text-[11px]" variant="outline">
                <VisibilityIcon className="h-3 w-3" />
                <span>{visibilityLabel}</span>
              </Badge>
            </div>

            <div className="flex items-center gap-1">
              {canFavorite && (
                <button
                  className="bg-card text-muted-foreground hover:border-warning hover:text-warning focus-visible:ring-ring rounded-full border p-1.5 transition focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                  type="button"
                  aria-label={
                    workflow.is_favorite ? t("tile.removeFavorite") : t("tile.markAsFavorite")
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleFavorite(workflow)
                  }}
                >
                  {workflow.is_favorite ? (
                    <Star className="fill-warning text-warning h-4 w-4" />
                  ) : (
                    <StarOff className="h-4 w-4" />
                  )}
                </button>
              )}

              <WorkflowTileDropdown
                canEdit={canEdit}
                deleteLabel={t("tile.deleteFlow")}
                duplicateLabel={t("tile.duplicate")}
                editLabel={t("tile.edit")}
                moreActionsLabel={t("tile.moreActions", { defaultValue: "More actions" })}
                workflow={workflow}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onOpen={onOpen}
                onOpenChange={setIsDropdownOpen}
              />
            </div>
          </div>

          <div
            className={cn(
              "overflow-hidden transition-[height] duration-300 ease-out",
              isCardExpanded && workflow.content ? "my-3 h-[180px]" : "h-0"
            )}
          >
            {workflow.content && (
              <WorkflowMiniPreview
                content={workflow.content}
                className={cn(
                  "bg-muted/30 h-[180px] w-full rounded-lg border transition-[opacity,scale] duration-300 ease-out",
                  isCardExpanded ? "scale-100 opacity-100" : "scale-75 opacity-0"
                )}
              />
            )}
          </div>

          <h3 className="mb-2 text-base leading-tight font-semibold">{workflow.title}</h3>

          {visibility === "company" && workflow.owner_name && (
            <div className="mb-2">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium",
                  "bg-muted text-muted-foreground"
                )}
              >
                <UserIcon className="h-3 w-3" />
                {workflow.owner_name}
              </span>
            </div>
          )}

          {workflow.description && (
            <p className="text-muted-foreground mb-3 line-clamp-2 text-sm leading-relaxed">
              {workflow.description}
            </p>
          )}

          {workflow.tags && workflow.tags.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {workflow.tags.map((tag) => (
                <Badge key={tag} className="text-[11px]" variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          <div className="mt-auto flex items-center justify-between border-t pt-3">
            <div ref={runButtonsRef} className="flex items-center gap-1.5">
              <ExpandableRunButtons
                explorerLabel={t("runDialog.runInExplorer")}
                isExpanded={isRunExpanded}
                runLabel={t("tile.run")}
                testLabel={t("runDialog.testInEditor")}
                workflow={workflow}
                workflowId={workflow.id}
                onExpand={() => setIsRunExpanded(true)}
              />
            </div>

            <div className="flex items-center gap-2">
              <div className="text-muted-foreground flex items-center gap-2 text-xs">
                {workflow.last_run && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{workflow.last_run}</span>
                  </span>
                )}
                {workflow.runs_this_week !== undefined && workflow.runs_this_week > 0 && (
                  <span className="text-muted-foreground/70">· {workflow.runs_this_week}/week</span>
                )}
              </div>
              {canSchedule && (
                <ScheduleButton
                  label={t("tile.scheduleRun")}
                  workflowId={workflow.id}
                  workflowTitle={workflow.title}
                />
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
